import { Controller } from "@hotwired/stimulus"
import Dropzone from "dropzone"

Dropzone.autoDiscover = false

export default class extends Controller {
  static targets = ["radio", "fileInput", "submitButton", "dropZone", "hiddenAttachmentId", "dropStatus"]
  static values = { taskId: String }

  connect() {
    this.initDropzone()
  }

  disconnect() {
    if (this.dropzone) this.dropzone.destroy()
  }

  // --- Dropzone ---

  initDropzone() {
    this.dropzone = new Dropzone(this.dropZoneTarget, {
      url: '/attachments',
      paramName: 'attachment[file]',
      maxFiles: 1,
      maxFilesize: 25,
      acceptedFiles: '.pdf,.xlsx,.docx,.csv,.txt',
      autoProcessQueue: true,
      clickable: false,
      previewsContainer: false, // no preview rendering — we handle status ourselves
      headers: {
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
      },
      sending: (_file, _xhr, formData) => {
        formData.append('attachment[attachable_type]', 'Task')
        formData.append('attachment[attachable_id]', this.taskIdValue)
      }
    })

    this.dropzone.on('addedfile', (file) => {
      this.deselectAll()
      this.setDropStatus('uploading', file.name)
      this.disableSubmit()
    })

    this.dropzone.on('success', (_file, response) => {
      if (response.success && this.hasHiddenAttachmentIdTarget) {
        this.hiddenAttachmentIdTarget.value = response.id
        this.setDropStatus('success', response.name)
        this.element.requestSubmit()
      } else {
        this.setDropStatus('error', response.errors?.[0] || 'Upload failed')
        this.enableSubmit()
      }
    })

    this.dropzone.on('error', (file, errorMessage) => {
      const msg = typeof errorMessage === 'string' ? errorMessage : errorMessage?.errors?.[0] || 'Upload failed'
      this.setDropStatus('error', msg)
      this.enableSubmit()
      this.dropzone.removeFile(file)
    })
  }

  // --- Radio / file input selection ---

  selectExisting() {
    if (this.hasFileInputTarget) this.fileInputTarget.value = ''
    this.clearDropStatus()
    this.clearHiddenAttachment()
    this.enableSubmit()
  }

  selectFile() {
    this.deselectRadios()
    this.clearHiddenAttachment()
    this.clearDropStatus()
    this.enableSubmit()
  }

  // --- Helpers ---

  deselectAll() {
    this.deselectRadios()
    if (this.hasFileInputTarget) this.fileInputTarget.value = ''
  }

  deselectRadios() {
    if (this.hasRadioTarget) this.radioTargets.forEach(r => r.checked = false)
  }

  enableSubmit() {
    if (this.hasSubmitButtonTarget) this.submitButtonTarget.disabled = false
  }

  disableSubmit() {
    if (this.hasSubmitButtonTarget) this.submitButtonTarget.disabled = true
  }

  clearHiddenAttachment() {
    if (this.hasHiddenAttachmentIdTarget) this.hiddenAttachmentIdTarget.value = ''
  }

  setDropStatus(state, detail) {
    if (!this.hasDropStatusTarget) return

    const escaped = detail.replace(/</g, '&lt;')
    switch (state) {
      case 'uploading':
        this.dropStatusTarget.innerHTML = `<i class="fas fa-spinner fa-spin me-1"></i>Uploading <strong>${escaped}</strong>...`
        this.dropStatusTarget.className = 'text-muted small mt-2'
        break
      case 'success':
        this.dropStatusTarget.innerHTML = `<i class="fas fa-check-circle text-success me-1"></i><strong>${escaped}</strong> uploaded`
        this.dropStatusTarget.className = 'text-success small mt-2'
        break
      case 'error':
        this.dropStatusTarget.innerHTML = `<i class="fas fa-exclamation-circle me-1"></i>${escaped}`
        this.dropStatusTarget.className = 'text-danger small mt-2'
        break
    }
  }

  clearDropStatus() {
    if (this.hasDropStatusTarget) {
      this.dropStatusTarget.innerHTML = ''
      this.dropStatusTarget.className = 'small mt-2'
    }
  }
}
